import _ from 'lodash';
import { defineStore } from 'pinia';
import { set, del, computed } from 'vue-demi';

import { useCommonProps } from './use-common-props';
import {
  PopupTab,
  ConferenceMember,
  CallInfo,
  CallControlsSettings,
  CALL_MEMBER_STATUS,
} from '../types';
import { CALL_MEMBER_STATUS_COLOR } from '../constants';

export const useDialerStore = defineStore('HITL-Dialer', {
  state: () => {
    return {
      isRinging: false,
      isWaiting: false,
      isJoined: false,
      isInProgress: false,
      isAnswering: false,

      contactId: '',
      sessionId: '',
      /** Usually a phone number */
      identifier: '',
      description: '',
      tabs: {
        invite: [] as PopupTab[],
        transfer: [] as PopupTab[],
      },
      membersById: {} as Record<string, ConferenceMember>,
      controls: {} as CallControlsSettings,

      isAgentMuted: false,
      isContactOnHold: false,
      isPutOnHoldActionLoading: false,
      isHangUpActionLoading: false,
      isLoadingMembers: false,
      isPerformingAction: false,
      isVoiceAPIReady: false,
    };
  },
});

/** TODO: Can be merged with store after "getCurrentInstance" is removed from "useRouter" */
export const useDialerGetters = () => {
  const store = useDialerStore();
  const props = useCommonProps();

  const agent = computed(() => {
    return props.getAgentById.value(props.agentId.value);
  });

  /**
   * We aren't able to add or transfer to agents in the current conference.
   * We can however add or transfer to agents that are disconnected, busy, etc.
   */
  const agentOptions = computed(() => {
    const statuses = Object.entries(CALL_MEMBER_STATUS_COLOR)
      .filter(entry => ['success', 'warning'].includes(entry[1]))
      .map(entry => entry[0]);

    return props.agents.value.filter(agent => {
      const member = store.membersById[agent.id];
      return !member || !statuses.includes(member.status);
    });
  });

  const contact = computed(() => {
    if (store.contactId) {
      return props.getContactById.value(store.contactId);
    }

    const number = store.identifier;
    if (number) {
      const phone = number.charAt(0) === '+' ? number : `+${number}`;
      return props.getContactByPhoneNumber.value(phone);
    }
  });

  const members = computed(() => Object.values(store.membersById));
  const session = computed(() => {
    if (store.sessionId) {
      return props.getSessionById.value(store.sessionId);
    }
  });

  /** Session "created at" timestamp in ms */
  const timestamp = computed(() => {
    return session.value ? new Date(session.value.createdAt || 0).getTime() : 0;
  });

  const isConnecting = computed(() => {
    return store.isWaiting
      || store.isRinging
      || store.isAnswering;
  });

  const isDialerVisible = computed(() => {
    return store.isRinging
      || store.isWaiting
      || store.isInProgress
      || store.isJoined
      || store.isAnswering;
  });

  const hasInviteButton = computed(() => {
    return store.controls.invite?.allowAgent
      || !!store.tabs.invite.length;
  });

  const hasTransferButton = computed(() => {
    return store.controls.transfer?.allowAgent ||
      !!store.tabs.transfer.length;
  });

  /* Hold and mute buttons are enabled by default */
  const hasMuteButton = computed(() => store.controls.mute !== false);
  const hasHoldButton = computed(() => store.controls.hold !== false);
  const hasKeypadButton = computed(() => {
    return !store.controls.emulateSoftPhone;
  });

  return {
    agent,
    contact,
    session,
    timestamp,
    agentOptions,
    members,
    isConnecting,
    isDialerVisible,
    hasKeypadButton,
    hasInviteButton,
    hasTransferButton,
    hasHoldButton,
    hasMuteButton,
  };
};

export const useDialerSetters = () => {
  const store = useDialerStore();

  const onIncomingCall = (identifier: string) => {
    store.isRinging = true;
    if (!store.identifier && identifier) {
      store.identifier = identifier;
    }
  };

  const onStartCall = (args: { info?: CallInfo; sessionId: string; }) => {
    if (!store.isRinging && !store.isInProgress) {
      store.isWaiting = true;
    }

    const { info = {} as Partial<CallInfo>, sessionId } = args;
    store.identifier = info.from || store.identifier;
    store.contactId = info.contactId || store.contactId;
    store.sessionId = sessionId || store.sessionId;
    store.description = info.description || '';
    store.tabs = Object.assign({}, store.tabs, info.tabs);
    store.membersById = {};
    store.controls = { ...info.controls };
    store.isAgentMuted = info.muted ?? store.isAgentMuted;
    store.isContactOnHold = info.onHold ?? store.isContactOnHold;
  };

  /** Set multiple state values and trigger a popup */
  const restore = (args: Partial<typeof store>) => {
    const keys = Object.keys(args);

    for (const key of keys) {
      (store as any)[key] = (args as any)[key];
    }
  };

  const answer = () => {
    if (!store.sessionId) {
      return;
    }

    store.isInProgress = true;
    store.isWaiting = false;
    store.isAnswering = false;
  };

  const onJoinCall = () => {
    store.isJoined = true;
    store.isRinging = false;
    store.isWaiting = false;
  };

  const onLeaveCall = () => {
    store.isRinging = false;
    store.isWaiting = false;
    store.isJoined = false;
    store.isInProgress = false;
    store.isAnswering = false;

    store.identifier = '';
    store.contactId = '';
    store.sessionId = '';
    store.description = '';
    store.tabs = { invite: [], transfer: [] };
    store.membersById = {};
    store.controls = {};

    store.isAgentMuted = false;
    store.isContactOnHold = false;
    store.isPutOnHoldActionLoading = false;
    store.isHangUpActionLoading = false;
    store.isLoadingMembers = false;
    store.isPerformingAction = false;
    store.isVoiceAPIReady = false;
  };

  const setMember = (member: ConferenceMember) => {
    set(store.membersById, member.memberId, member);
  };

  const removeMember = (memberId: string) => {
    del(store.membersById, memberId);
  };

  const setMembers = (args: typeof store.membersById) => {
    const LEAVE_EVENTS = [
      CALL_MEMBER_STATUS.BUSY,
      CALL_MEMBER_STATUS.DISCONNECTED,
      CALL_MEMBER_STATUS.NOT_CONNECTED,
      CALL_MEMBER_STATUS.ERROR,
      CALL_MEMBER_STATUS.HANG_UP,
    ];

    store.membersById = _.keyBy(
      Object.values(args).filter(member => !LEAVE_EVENTS.includes(member.status)),
      'memberId'
    );
  };

  return {
    onIncomingCall,
    onStartCall,
    restore,
    answer,
    onJoinCall,
    onLeaveCall,
    setMember,
    removeMember,
    setMembers,
  };
};
